import { assistant } from '../clients';
import { INSTANCE_URL } from '@/config/env';

/**
 * Session generation request payload
 */
export interface CreateSessionRequest {
  logon_at: string;
  ip_address: string;
  user_agent: string;
  status: string;
  login_method: string;
}

/**
 * Session generation response from API
 */
export interface CreateSessionResponse {
  session_id?: string;
  user_id?: string;
  status?: string;
  [key: string]: unknown;
}

/**
 * Parameters for createSession including auth headers
 */
export interface CreateSessionParams {
  accessToken: string;
  sessionId: string;
  payload?: Partial<CreateSessionRequest>;
}

/**
 * Get current timestamp in ISO format for logon_at
 */
const getCurrentTimestamp = (): string => {
  return new Date().toISOString().slice(0, 23); // Format: 2026-01-14T13:13:13.754
};

/**
 * Get user agent string with fallback
 */
const getUserAgent = (): string => {
  if (typeof navigator !== 'undefined' && navigator.userAgent) {
    return navigator.userAgent;
  }
  return 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/141.0.0.0 Safari/537.36';
};

/**
 * Session Service
 * Handles API calls related to user session generation
 */
export const sessionService = {
  /**
   * Create a new user session
   * @param params - Session creation parameters including auth headers
   * @returns Promise with session response
   */
  async createSession(
    params: CreateSessionParams
  ): Promise<CreateSessionResponse> {
    const { accessToken, sessionId, payload = {} } = params;

    // Build request payload with dynamic values and defaults
    const requestPayload: CreateSessionRequest = {
      logon_at: payload.logon_at || getCurrentTimestamp(),
      ip_address: payload.ip_address || `${INSTANCE_URL}/assistant`,
      user_agent: payload.user_agent || getUserAgent(),
      status: payload.status || 'active',
      login_method: payload.login_method || 'oauth',
    };

    // Use assistant client with custom headers
    const response = await assistant<CreateSessionResponse>(
      '/api/v1/user_session',
      'POST',
      requestPayload,
      {
        Authorization: `Bearer ${accessToken}`,
        'x-user-session-id': sessionId,
      }
    );

    return response.data;
  },
};

export default sessionService;
